import { motion, AnimatePresence } from "motion/react";
import {
    ArrowLeft,
    MapPin,
    Calendar,
    Users,
    Shield,
    TrendingUp,
    FileText,
    ExternalLink,
    CheckCircle,
    AlertTriangle,
    Lock,
    Building2,
    Globe,
    Gem,
    Image as ImageIcon,
    ChevronLeft,
    ChevronRight,
    X,
    Info
} from "lucide-react";
import { useState, useMemo } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

import { useToast } from "../components/ui-feedback";
import { useRWAStore, Asset } from "../store/rwa-store";

type TabType = 'overview' | 'financials' | 'documents' | 'location';

interface AssetDetailViewProps {
    assetId: string;
    onClose: () => void;
}

export default function AssetDetailView({ assetId, onClose }: AssetDetailViewProps) {
    const { assets, currentUser, updatePortfolio, deposit: walletDeposit } = useRWAStore();
    const { showToast } = useToast();
    const [activeTab, setActiveTab] = useState<TabType>('overview');
    const [investAmount, setInvestAmount] = useState("");
    const [processing, setProcessing] = useState(false);
    const [imageIndex, setImageIndex] = useState(0);
    const [showLightbox, setShowLightbox] = useState(false);

    const asset = assets.find(a => a.id === assetId);

    const mockYieldData = [
        { month: 'Jan', yield: 1.2 },
        { month: 'Feb', yield: 1.3 },
        { month: 'Mar', yield: 1.1 },
        { month: 'Apr', yield: 1.5 },
        { month: 'May', yield: 1.4 },
        { month: 'Jun', yield: 1.6 },
        { month: 'Jul', yield: 1.8 },
        { month: 'Aug', yield: 1.7 },
        { month: 'Sep', yield: 1.9 },
        { month: 'Oct', yield: 2.0 },
        { month: 'Nov', yield: 2.1 },
        { month: 'Dec', yield: 2.2 },
    ];

    const estimatedTokens = useMemo(() => {
        if (!asset || !investAmount) return 0;
        return parseFloat(investAmount) / asset.price;
    }, [investAmount, asset]);

    const userBalance = currentUser?.wallet?.balances.USD || 0;
    const canInvest = parseFloat(investAmount) > 0 && parseFloat(investAmount) <= userBalance;

    const handleInvest = () => {
        if (!canInvest || !asset) return;
        setProcessing(true);

        setTimeout(() => {
            const amount = parseFloat(investAmount);
            const tokens = amount / asset.price;

            // Deduct from wallet
            walletDeposit(-amount, 'USD');

            // Add to portfolio
            updatePortfolio(asset.id, tokens, asset.price);

            // Show success toast
            showToast('success', 'Investment Confirmed!', `Successfully invested $${amount.toLocaleString()} in ${asset.symbol}`);

            setProcessing(false);
            onClose();
        }, 2000);
    };

    const _getAssetIcon = (type: string) => {
        switch (type) {
            case 'real-estate': return Building2;
            case 'bond': return FileText;
            case 'commodity': return Gem;
            case 'art': return ImageIcon;
            case 'crypto': return Globe;
            default: return Globe;
        }
    };

    // Static icon mapping to avoid creating components during render
    const ASSET_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
        'real-estate': Building2,
        'bond': FileText,
        'commodity': Gem,
        'art': ImageIcon,
        'crypto': Globe,
    };

    const getRiskLabel = (rating: number) => {
        if (rating <= 2) return { label: 'Low Risk', color: 'text-emerald-500', bg: 'bg-emerald-500/10' };
        if (rating <= 3) return { label: 'Medium Risk', color: 'text-amber-500', bg: 'bg-amber-500/10' };
        return { label: 'High Risk', color: 'text-red-500', bg: 'bg-red-500/10' };
    };

    if (!asset) return null;

    const AssetIconComponent = ASSET_ICONS[asset.type] || Globe;
    const riskInfo = getRiskLabel(asset.riskRating);
    const fundingPercent = asset.targetAmount ? (asset.fundedAmount || 0) / asset.targetAmount * 100 : 100;

    return (
        <div className="fixed inset-0 z-50 bg-neutral-950 overflow-y-auto">
            {/* Header */}
            <header className="sticky top-0 z-10 bg-neutral-950/80 backdrop-blur-xl border-b border-neutral-900">
                <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        <span>Back to Marketplace</span>
                    </button>
                    <div className="flex items-center gap-4">
                        <span className="text-xs font-mono text-neutral-500">{asset.symbol}</span>
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${riskInfo.bg} ${riskInfo.color}`}>
                            {riskInfo.label}
                        </span>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Hero Image Gallery */}
                        <div className="relative aspect-video rounded-2xl overflow-hidden bg-neutral-900 group">
                            {asset.images?.[imageIndex] ? (
                                <>
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                        src={asset.images[imageIndex]}
                                        alt={asset.name}
                                        className="w-full h-full object-cover cursor-pointer"
                                        onClick={() => setShowLightbox(true)}
                                    />
                                    {/* Image Navigation */}
                                    {asset.images.length > 1 && (
                                        <>
                                            <button
                                                onClick={() => setImageIndex(i => i > 0 ? i - 1 : asset.images.length - 1)}
                                                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <ChevronLeft className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => setImageIndex(i => i < asset.images.length - 1 ? i + 1 : 0)}
                                                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <ChevronRight className="w-5 h-5" />
                                            </button>
                                            {/* Dots */}
                                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                                                {asset.images.map((_, i) => (
                                                    <button
                                                        key={i}
                                                        onClick={() => setImageIndex(i)}
                                                        className={`w-2 h-2 rounded-full transition-colors ${imageIndex === i ? 'bg-white' : 'bg-white/40'}`}
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </>
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <AssetIconComponent className="w-16 h-16 text-neutral-700" />
                                </div>
                            )}
                        </div>

                        {/* Asset Title */}
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <div className="flex items-center gap-3 mb-2">
                                    <div className={`p-2 rounded-lg ${asset.type === 'real-estate' ? 'bg-blue-500/10 text-blue-500' : asset.type === 'bond' ? 'bg-purple-500/10 text-purple-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
                                        <AssetIconComponent className="w-5 h-5" />
                                    </div>
                                    <span className="text-sm text-neutral-500 capitalize">{asset.type.replace('-', ' ')}</span>
                                </div>
                                <h1 className="text-3xl font-bold mb-2">{asset.name}</h1>
                                <div className="flex items-center gap-4 text-sm text-neutral-500">
                                    {asset.issuer.verified && (
                                        <span className="flex items-center gap-1">
                                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                                            Verified Issuer
                                        </span>
                                    )}
                                    <span className="flex items-center gap-1">
                                        <Users className="w-4 h-4" />
                                        {asset.investorCount.toLocaleString()} investors
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Key Metrics Banner */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <MetricCard
                                label="Token Price"
                                value={`$${asset.price.toLocaleString()}`}
                                icon={TrendingUp}
                                trend={asset.change24h}
                            />
                            {asset.apy && (
                                <MetricCard
                                    label="Expected APY"
                                    value={`${asset.apy}%`}
                                    icon={TrendingUp}
                                    positive
                                />
                            )}
                            <MetricCard
                                label="Min Investment"
                                value={`$${asset.minInvestment.toLocaleString()}`}
                                icon={Shield}
                            />
                            {asset.term && (
                                <MetricCard
                                    label="Term"
                                    value={asset.term}
                                    icon={Calendar}
                                />
                            )}
                        </div>

                        {/* Funding Progress */}
                        {asset.targetAmount && (
                            <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <span className="text-sm text-neutral-400">Funding Progress</span>
                                    <span className="text-sm font-mono">{fundingPercent.toFixed(1)}%</span>
                                </div>
                                <div className="h-3 bg-neutral-800 rounded-full overflow-hidden mb-4">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${fundingPercent}%` }}
                                        className="h-full bg-linear-to-r from-emerald-500 to-cyan-500"
                                    />
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="font-mono">${(asset.fundedAmount || 0).toLocaleString()} raised</span>
                                    <span className="text-neutral-500">of ${asset.targetAmount.toLocaleString()}</span>
                                </div>
                            </div>
                        )}

                        {/* Tabs */}
                        <div className="border-b border-neutral-800">
                            <div className="flex gap-8">
                                {(['overview', 'financials', 'documents', 'location'] as TabType[]).map(tab => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        className={`pb-4 px-1 text-sm font-medium capitalize border-b-2 transition-colors ${activeTab === tab
                                            ? 'border-white text-white'
                                            : 'border-transparent text-neutral-500 hover:text-neutral-300'
                                            }`}
                                    >
                                        {tab}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Tab Content */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="min-h-[300px]"
                            >
                                {activeTab === 'overview' && (
                                    <div className="space-y-6">
                                        <div>
                                            <h3 className="text-lg font-bold mb-4">About This Investment</h3>
                                            <p className="text-neutral-400 leading-relaxed">{asset.description}</p>
                                        </div>

                                        <div>
                                            <h3 className="text-lg font-bold mb-4">Issuer Information</h3>
                                            <div className="bg-neutral-900/40 border border-neutral-800 rounded-xl p-4 flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-neutral-800 rounded-full flex items-center justify-center">
                                                        <Building2 className="w-5 h-5 text-neutral-400" />
                                                    </div>
                                                    <div>
                                                        <div className="font-medium">{asset.issuer.name}</div>
                                                        {asset.issuer.verified && (
                                                            <div className="text-xs text-emerald-500 flex items-center gap-1">
                                                                <CheckCircle className="w-3 h-3" />
                                                                Verified
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <button className="text-sm text-neutral-400 hover:text-white flex items-center gap-1">
                                                    View Profile <ExternalLink className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {asset.tags.length > 0 && (
                                            <div>
                                                <h3 className="text-lg font-bold mb-4">Tags</h3>
                                                <div className="flex flex-wrap gap-2">
                                                    {asset.tags.map(tag => (
                                                        <span key={tag} className="px-3 py-1 bg-neutral-800 rounded-full text-sm text-neutral-400 capitalize">
                                                            {tag.replace('-', ' ')}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'financials' && (
                                    <div className="space-y-6">
                                        {asset.apy && (
                                            <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6">
                                                <h3 className="text-lg font-bold mb-6">Projected Yield Schedule</h3>
                                                <div className="h-48">
                                                    <ResponsiveContainer>
                                                        <AreaChart data={mockYieldData}>
                                                            <defs>
                                                                <linearGradient id="yieldGradient" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                                                </linearGradient>
                                                            </defs>
                                                            <XAxis dataKey="month" stroke="#525252" fontSize={12} tickLine={false} axisLine={false} />
                                                            <YAxis stroke="#525252" fontSize={12} tickLine={false} axisLine={false} tickFormatter={v => `${v}%`} />
                                                            <Tooltip contentStyle={{ backgroundColor: '#171717', border: '1px solid #262626', borderRadius: '8px' }} />
                                                            <Area type="monotone" dataKey="yield" stroke="#10b981" strokeWidth={2} fill="url(#yieldGradient)" />
                                                        </AreaChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="bg-neutral-900/40 border border-neutral-800 rounded-xl p-4">
                                                <div className="text-sm text-neutral-500 mb-1">Launch Date</div>
                                                <div className="font-medium">{new Date(asset.launchDate).toLocaleDateString()}</div>
                                            </div>
                                            {asset.maturityDate && (
                                                <div className="bg-neutral-900/40 border border-neutral-800 rounded-xl p-4">
                                                    <div className="text-sm text-neutral-500 mb-1">Maturity Date</div>
                                                    <div className="font-medium">{new Date(asset.maturityDate).toLocaleDateString()}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'documents' && (
                                    <div className="space-y-4">
                                        {asset.documents.length > 0 ? asset.documents.map((doc, i) => (
                                            <a
                                                key={i}
                                                href={doc.url}
                                                className="flex items-center justify-between p-4 bg-neutral-900/40 border border-neutral-800 rounded-xl hover:border-neutral-700 transition-colors group"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-neutral-800 rounded-lg flex items-center justify-center">
                                                        <FileText className="w-5 h-5 text-neutral-400" />
                                                    </div>
                                                    <div>
                                                        <div className="font-medium">{doc.name}</div>
                                                        <div className="text-xs text-neutral-500 uppercase">{doc.type}</div>
                                                    </div>
                                                </div>
                                                <ExternalLink className="w-4 h-4 text-neutral-500 group-hover:text-white transition-colors" />
                                            </a>
                                        )) : (
                                            <div className="text-center py-12 text-neutral-500">
                                                No documents available
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'location' && (
                                    <div className="space-y-4">
                                        {asset.location ? (
                                            <>
                                                <div className="aspect-video bg-neutral-900 rounded-2xl overflow-hidden">
                                                    {/* Map Placeholder */}
                                                    <div className="w-full h-full flex items-center justify-center bg-linear-to-br from-neutral-900 to-neutral-800">
                                                        <div className="text-center">
                                                            <MapPin className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
                                                            <div className="text-neutral-500">Interactive Map</div>
                                                            <div className="text-xs text-neutral-600 mt-1">(Integration available)</div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="bg-neutral-900/40 border border-neutral-800 rounded-xl p-4">
                                                    <div className="flex items-start gap-3">
                                                        <MapPin className="w-5 h-5 text-neutral-400 mt-0.5" />
                                                        <div>
                                                            <div className="font-medium">{asset.location.address}</div>
                                                            <div className="text-sm text-neutral-500">{asset.location.city}, {asset.location.country}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-center py-12 text-neutral-500">
                                                No location information available
                                            </div>
                                        )}
                                    </div>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Investment Widget (Sticky Sidebar) */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-24">
                            <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6 space-y-6">
                                <h3 className="text-lg font-bold">Invest in {asset.symbol}</h3>

                                {/* Price Info */}
                                <div className="bg-neutral-950 p-4 rounded-xl">
                                    <div className="flex justify-between items-center text-sm mb-2">
                                        <span className="text-neutral-500">Token Price</span>
                                        <span className="font-mono">${asset.price.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-neutral-500">Your Balance</span>
                                        <span className="font-mono text-emerald-500">${userBalance.toLocaleString()}</span>
                                    </div>
                                </div>

                                {/* Amount Input */}
                                <div>
                                    <label className="block text-sm font-medium text-neutral-400 mb-2">Investment Amount (USD)</label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 text-lg">$</span>
                                        <input
                                            type="number"
                                            value={investAmount}
                                            onChange={e => setInvestAmount(e.target.value)}
                                            placeholder="0.00"
                                            min={asset.minInvestment}
                                            className="w-full bg-neutral-950 border border-neutral-800 rounded-xl py-4 pl-10 pr-4 text-xl font-mono focus:outline-hidden focus:border-emerald-500"
                                        />
                                    </div>
                                    <div className="flex gap-2 mt-2">
                                        {[500, 1000, 2500, 5000].map(preset => (
                                            <button
                                                key={preset}
                                                onClick={() => setInvestAmount(preset.toString())}
                                                className="flex-1 py-2 bg-neutral-800 rounded-lg text-xs font-mono hover:bg-neutral-700 transition-colors"
                                            >
                                                ${preset}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Estimation */}
                                <div className="space-y-3 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-neutral-500">Est. Tokens</span>
                                        <span className="font-mono">{estimatedTokens.toFixed(4)} {asset.symbol}</span>
                                    </div>
                                    {asset.apy && (
                                        <div className="flex justify-between">
                                            <span className="text-neutral-500">Est. Annual Yield</span>
                                            <span className="font-mono text-emerald-500">
                                                ${((parseFloat(investAmount) || 0) * (asset.apy / 100)).toFixed(2)}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <span className="text-neutral-500">Platform Fee</span>
                                        <span className="font-mono text-emerald-500">$0.00</span>
                                    </div>
                                </div>

                                {/* Warnings */}
                                {parseFloat(investAmount) > userBalance && (
                                    <div className="flex items-center gap-2 text-red-500 text-sm p-3 bg-red-500/10 rounded-lg">
                                        <AlertTriangle className="w-4 h-4" />
                                        <span>Insufficient balance</span>
                                    </div>
                                )}

                                {parseFloat(investAmount) < asset.minInvestment && parseFloat(investAmount) > 0 && (
                                    <div className="flex items-center gap-2 text-amber-500 text-sm p-3 bg-amber-500/10 rounded-lg">
                                        <Info className="w-4 h-4" />
                                        <span>Minimum investment is ${asset.minInvestment}</span>
                                    </div>
                                )}

                                {/* Invest Button */}
                                <button
                                    onClick={handleInvest}
                                    disabled={!canInvest || processing || parseFloat(investAmount) < asset.minInvestment}
                                    className="w-full py-4 bg-emerald-500 text-black font-bold rounded-xl hover:bg-emerald-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {processing ? (
                                        <>
                                            <motion.div
                                                animate={{ rotate: 360 }}
                                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full"
                                            />
                                            Signing Transaction...
                                        </>
                                    ) : (
                                        <>
                                            <Lock className="w-4 h-4" />
                                            Confirm Investment
                                        </>
                                    )}
                                </button>

                                <p className="text-xs text-neutral-500 text-center">
                                    By investing, you agree to our terms of service and understand the risks involved.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Similar Assets */}
                <div className="mt-12">
                    <h3 className="text-xl font-bold mb-6">Similar Assets</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {assets
                            .filter(a => a.id !== asset.id && a.type === asset.type)
                            .slice(0, 3)
                            .map(similarAsset => (
                                <SimilarAssetCard key={similarAsset.id} asset={similarAsset} />
                            ))}
                    </div>
                </div>
            </div>

            {/* Lightbox */}
            <AnimatePresence>
                {showLightbox && asset.images?.[imageIndex] && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-60 bg-black/95 flex items-center justify-center p-4"
                        onClick={() => setShowLightbox(false)}
                    >
                        <button className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full">
                            <X className="w-6 h-6" />
                        </button>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            src={asset.images[imageIndex]}
                            alt={asset.name}
                            className="max-w-full max-h-full object-contain"
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// Metric Card Component
interface MetricCardProps {
    label: string;
    value: string;
    icon: React.ComponentType<{ className?: string }>;
    trend?: number;
    positive?: boolean;
}

function MetricCard({ label, value, icon: Icon, trend, positive: _positive }: MetricCardProps) {
    return (
        <div className="bg-neutral-900/40 border border-neutral-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4 text-neutral-500" />
                <span className="text-xs text-neutral-500">{label}</span>
            </div>
            <div className="font-bold font-mono text-lg">{value}</div>
            {trend !== undefined && (
                <div className={`text-xs mt-1 ${trend >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {trend >= 0 ? '+' : ''}{trend}%
                </div>
            )}
        </div>
    );
}

// Similar Asset Card Component
function SimilarAssetCard({ asset }: { asset: Asset }) {
    return (
        <div className="bg-neutral-900/40 border border-neutral-800 rounded-xl p-4 hover:border-neutral-700 transition-colors cursor-pointer">
            <div className="flex items-center gap-3 mb-3">
                {asset.images?.[0] && (
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-neutral-800">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={asset.images[0]} alt={asset.name} className="w-full h-full object-cover" />
                    </div>
                )}
                <div>
                    <div className="font-medium">{asset.name}</div>
                    <div className="text-xs text-neutral-500">{asset.symbol}</div>
                </div>
            </div>
            <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Price</span>
                <span className="font-mono">${asset.price.toLocaleString()}</span>
            </div>
            {asset.apy && (
                <div className="flex justify-between text-sm mt-1">
                    <span className="text-neutral-500">APY</span>
                    <span className="font-mono text-emerald-500">{asset.apy}%</span>
                </div>
            )}
        </div>
    );
}
